import type * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import type { PhaseRenderContext } from '@/shared/types/assets'

export interface SceneInViewSubmitOptions {
  /** 提交期间临时替换相机 layer 掩码；提交后必定恢复。 */
  readonly layerMask?: number
}

/**
 * 独立场景（工具/Overlay、角色、胶囊指示器等）在当前 View 的统一提交入口。
 *
 * 契约与 `GraphicsBackend.renderScene` 一致：必须在活动 frame/View 内调用，viewport/scissor、
 * output target 与 renderer 状态由 backend 依当前 View 决定，调用方不得直接改写 legacy renderer。
 * 这里只保留领域语义（可选的相机 layer 掩码），状态 capture/restore 由 RenderPipeline 负责。
 */
export function submitSceneInView(
  context: PhaseRenderContext,
  scene: THREE.Scene,
  camera: THREE.Camera,
  options: SceneInViewSubmitOptions = {}
): void {
  const { graphics } = context
  const renderScene = graphics.renderScene
  if (typeof renderScene !== 'function') {
    throw new UnsupportedGraphicsCapabilityError(
      'scene-surface-render',
      graphics.kind,
      '要求 GraphicsBackend.renderScene 受控 surface 提交原语'
    )
  }

  const savedLayerMask = camera.layers.mask
  if (options.layerMask !== undefined) {
    camera.layers.mask = options.layerMask
  }

  try {
    renderScene.call(graphics, scene, camera)
  } finally {
    camera.layers.mask = savedLayerMask
  }
}
